import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { GanacheCalculatorClient } from './ui/GanacheCalculatorClient';

interface GanacheCalculatorProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const GanacheCalculator: React.FC<GanacheCalculatorProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Ganache Ratio Calculator"
            description="Easily calculate the correct chocolate to cream ratios for any type of ganache. Create the perfect consistency for drips, fillings, or frosting."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="ganache-calculator"
        >
            <GanacheCalculatorClient />
            <GanacheCalculatorContent />
        </ToolContainer>
    );
};

const GanacheCalculatorContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border text-brand-text-body">
        {/* SEO Intro Section */}
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Master Chocolate Ganache Ratios</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Chocolate ganache is one of the most versatile recipes in baking, used for everything from <strong>drip cakes</strong> and <strong>truffles</strong> to <strong>macaron fillings</strong> and <strong>whipped frosting</strong>. But getting the texture right depends entirely on the <strong>chocolate-to-cream ratio</strong>. Our <strong>Ganache Calculator</strong> does the math for you. Whether you're using white, milk, or dark chocolate, simply select your desired consistency, and we'll tell you exactly how much heavy cream to add.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    Understanding Ganache Ratios
                </h3>
                <ul className="space-y-3 text-sm text-brand-text-body/80 list-disc pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>1:1 Ratio (Semi-Sweet):</strong> Equal parts chocolate and cream. Perfect for <strong>glazes</strong> and <strong>drips</strong>.</li>
                    <li><strong>2:1 Ratio (Semi-Sweet):</strong> Two parts chocolate to one part cream. Firm enough for <strong>truffles</strong> or piping.</li>
                    <li><strong>3:1 Ratio (White Chocolate):</strong> Needs more chocolate due to lace of cocoa solids.</li>
                </ul>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            Why did my ganache split?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Splitting happens if the cream is too hot or you stir too vigorously. Try whisking in a tablespoon of cold heavy cream.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
